import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import moment from 'moment'
import { useSelector } from 'react-redux'

import { selectUser } from '../../../../reducers/UserReducer'
import Settings from './Settings'
import SchoolQuotasTable from './SchoolQuotasTable'
import SchoolQuotasHeader from './SchoolQuotasHeader'
import QuotaFormModalProvider from '../../../../Providers/Forms/QuotaForm/QuotaFormModalProvider'
import QuotaToolBar from './QuotaToolBar'
import { InstitutionsQuotasSettings } from '../../../../Components/antd/Tables/DragTable/OverlaySettings'
import { useLocalStorage } from '../../../../hooks/UseLocalStorage'
import LOCAL_STORAGE_KEYS, { localStorage } from '../../../../utils/localStorage'
import { getSectorQuotas, getSectorStudentPathways } from '../../../../utils/api/sector'
import { firstOrDefault, getIdOrNull, isNonEmptyArray, isObject } from '../../../../utils'
import { InstitutionContext } from '../../../../Providers/InstitutionProvider'
import { SerializationContexts } from '../../../../utils/api/quota'
import { getQuotasMap, selectionToQuotas, updateQuotaByActions } from '../../../../Components/shared/Quotas/utils'
import { buildSchoolQuota, buildSectionToYearQuota, isQuotaInSchools, isQuotaInSectionToYears } from './utils'
import { TimePeriod, PeriodTypes } from '../../../../utils/timePeriods'
import useRequest from '../../../../hooks/useRequest'
import { getSchoolPeriods } from '../../../../utils/api/schoolPeriods'
import { SchoolPeriod } from '../../../../utils/entities/schoolPeriod'
import { OrderBy, OrderDirections } from '../../../../utils/constants'
import { Quota, QuotaValidators } from '../../../../utils/entities/quota'
import { API_DATE, arePeriodsOverlapping } from '../../../../utils/momentjs'
import { getBySector } from '../../../../utils/api/sectionGroup'
import { findSection, SectionGroup } from '../../../../utils/entities/sectionGroup'
import useQuota from '../../../../utils/api/hooks/useQuota'
import { Properties } from '../../../../utils/entities'
import { DEFAULT_SELECTED_VALUES } from '../../../../Components/antd/Tables/DragTable/DragTable'
import Expandable from '../../../../Components/shared/Expandable'

import '../../../../assets/quota-allocation.scss'

const KEYS_TO_VALIDATE = Object.keys(QuotaValidators)

const SchoolQuotasView = () => {
  const { coloredSectionToYears, coloredSchools } = useContext(InstitutionContext)
  const user = useSelector(selectUser)

  const [period, setPeriod] = useState(new TimePeriod(moment(), 26, PeriodTypes.WEEK))
  const [sector, setSector] = useState(null)
  const [rawQuotas, setRawQuotas] = useState([])
  const [studentPathways, setStudentPathways] = useState([])
  const [sectionGroups, setSectionGroups] = useState([])
  const [isYearly, setIsYearly] = useState(false)
  const [selectedQuotas, setSelectedQuotas] = useState({ values: [], selectedCell: null })
  const [schools, setSchools] = useLocalStorage(LOCAL_STORAGE_KEYS.QUOTAS_TABLE.SELECTED_SCHOOLS, [])
  const [sectionToYears, setSectionToYears] = useLocalStorage(LOCAL_STORAGE_KEYS.QUOTAS_TABLE.SELECTED_SECTION_TO_YEARS, [])
  const [overlaySettings, setOverlaySettings] = useLocalStorage(LOCAL_STORAGE_KEYS.QUOTAS_TABLE.OVERLAY_SETTINGS, new InstitutionsQuotasSettings({ displayPlacesRequests: true }), (value) => new InstitutionsQuotasSettings(value))
  const [isUpdating, setIsUpdating] = useState(false)
  const [coloredEntity, setColoredEntity] = useState(Properties.SCHOOL)

  const config = useMemo(() => ({ overlay: overlaySettings }), [overlaySettings])

  useEffect(() => {
    if (isObject(sector)) {
      const { startDate, endDate } = period

      setRawQuotas(quotas => quotas.filter(q => arePeriodsOverlapping(period, q)))

      getSectorQuotas(user, sector, { startDate: startDate.format(API_DATE), endDate: endDate.format(API_DATE), contexts: [SerializationContexts.LABELS] }).then(({ data }) => {
        setRawQuotas(data)
      })

      getSectorStudentPathways(user, sector, { contexts: [SerializationContexts.SECTION] }).then(({ data }) => {
        setStudentPathways(data)
      })

      getBySector(user, sector).then(({ data }) => {
        setSectionGroups(data.map(group => new SectionGroup(group)))
      })
    }
  }, [sector, user.id, period])

  const rawQuotasMap = useMemo(() => getQuotasMap(rawQuotas, q => {
    let quota

    if (coloredEntity === Properties.SCHOOL) {
      quota = buildSchoolQuota(q, coloredSchools)
    } else if (coloredEntity === Properties.SECTION_TO_YEAR) {
      quota = buildSectionToYearQuota(q, coloredSectionToYears)
    }

    return quota
  }), [coloredEntity, coloredSchools, coloredSectionToYears, rawQuotas])

  const quotasMap = useMemo(() => {
    const copyQuotasMap = { ...rawQuotasMap }

    if (coloredEntity === Properties.SCHOOL && overlaySettings.filterQuotasBySchools) {
      copyQuotasMap.quotas = copyQuotasMap.quotas.filter(isQuotaInSchools(schools))
    } else if (coloredEntity === Properties.SECTION_TO_YEAR && isNonEmptyArray(sectionToYears)) {
      copyQuotasMap.quotas = copyQuotasMap.quotas.filter(isQuotaInSectionToYears(sectionToYears))
    }

    if (overlaySettings.displayPlacesRequests) {
      if (overlaySettings.filterPlacesRequestsBySchools) {
        copyQuotasMap.desideratas = copyQuotasMap.desideratas.filter(isQuotaInSchools(schools))
      }

      copyQuotasMap.quotas = copyQuotasMap.quotas.concat(copyQuotasMap.desideratas)
    }

    return copyQuotasMap
  }, [coloredEntity, rawQuotasMap, sectionGroups, schools, sectionToYears, overlaySettings])

  const quotasToUpdate = useMemo(() => {
    const quotas = []

    if (isNonEmptyArray(sectionGroups) && !isUpdating) {
      quotasMap.quotas.filter(q => isObject(q.section)).forEach(q => {
        const sectionGroup = sectionGroups.find(g => findSection(g, q.section))

        if (isObject(sectionGroup) && !quotas.some(quota => quota.id === q.id)) {
          quotas.push({ ...q, sectionGroup, section: null })
        }
      })
    }

    return quotas
  }, [quotasMap, sectionGroups, isUpdating])

  const handleQuotaChange = useCallback((...params) => {
    const { data: newQuotas, isDeleteAction } = updateQuotaByActions([...rawQuotas], params, period, LOCAL_STORAGE_KEYS.QUOTAS_TABLE.QUOTAS_MATRIX)

    setRawQuotas(newQuotas)

    if (isDeleteAction) {
      setSelectedQuotas(DEFAULT_SELECTED_VALUES)
    }
  }, [rawQuotas, period])

  const quotaHandler = useQuota({ callback: handleQuotaChange })

  useEffect(() => {
    if (!isUpdating && isNonEmptyArray(quotasToUpdate)) {
      setIsUpdating(true)

      quotaHandler.update(quotasToUpdate.map(q => new Quota(q).toRequestBody(true))).then(() => {
        setIsUpdating(false)
      })
    }
  }, [isUpdating, quotasToUpdate, quotaHandler.update])

  const handleDateChange = useCallback(date => {
    setSelectedQuotas(DEFAULT_SELECTED_VALUES)
    setPeriod(period => new TimePeriod(date, period.quantity, PeriodTypes.WEEK))
  }, [])

  const handlePeriodChange = useCallback(quantity => {
    setSelectedQuotas(DEFAULT_SELECTED_VALUES)
    setPeriod(period => new TimePeriod(period.startDate, quantity, PeriodTypes.WEEK))
  }, [])

  const handleSelectSetting = useCallback((setting, isSelected) => {
    setOverlaySettings(current => current.clone({ [setting]: isSelected }))
  }, [setOverlaySettings])

  const holidays = useRequest({
    request: getSchoolPeriods,
    enabled: schools.length === 1,
    parameters: useMemo(() => ({
      filters: { school: getIdOrNull(schools[0]) },
      order: { by: OrderBy.START_DATE, direction: OrderDirections.ASC }
    }), [schools[0]]),
    select: useCallback(holidays => holidays.map(holiday => getIdOrNull(holiday) ? new SchoolPeriod(holiday) : null), [])
  })

  const handleResetGravity = useCallback(() => {
    quotasMap.quotas.forEach(q => {
      localStorage.removeValue(LOCAL_STORAGE_KEYS.QUOTAS_TABLE.QUOTAS_MATRIX, q.id)
    })
    quotasMap.desideratas.forEach(q => {
      localStorage.removeValue(LOCAL_STORAGE_KEYS.QUOTAS_TABLE.QUOTAS_MATRIX, q.id)
    })

    setRawQuotas(quotas => [...quotas])
  }, [quotasMap])

  return (
    <div className='school-quotas-container flex-row h-100 w-100'>
      <QuotaFormModalProvider
        sectionGroups={isObject(sector) ? { [getIdOrNull(sector)]: sectionGroups } : []}
        storageKey={LOCAL_STORAGE_KEYS.QUOTAS_TABLE.QUOTAS_MATRIX}
        studentPathways={studentPathways}
        keysToValidate={KEYS_TO_VALIDATE}
      >
        <Expandable>
          <Settings
            coloredEntity={coloredEntity}
            sectionToYears={sectionToYears}
            schools={schools}
            onSelectSector={setSector}
            onSelectSchool={setSchools}
            onSelectSectionToYear={setSectionToYears}
          />
        </Expandable>
        <div className='quotas-table-container h-100'>
          <div className='quotas-table-header pd-btm-8'>
            <SchoolQuotasHeader
              coloredEntity={coloredEntity}
              onColoredEntityChange={setColoredEntity}
              sector={sector}
              period={period}
              isYearly={isYearly}
              selectedQuota={selectedQuotas}
              overlaySettings={overlaySettings}
              onDateChange={handleDateChange}
              onPeriodChange={handlePeriodChange}
              onIsYearlyChange={setIsYearly}
              onQuotaChange={handleQuotaChange}
              onOverlaySettingsSelection={handleSelectSetting}
              onResetGravity={handleResetGravity}
            />
          </div>
          <div className='quotas-table-content' style={{ width: 'calc(100% - 5px)' }}>
            <SchoolQuotasTable
              coloredEntity={coloredEntity}
              isYearly={isYearly}
              school={firstOrDefault(schools)}
              sector={sector}
              studentPathways={studentPathways}
              sectionGroups={sectionGroups}
              period={period}
              holidayPeriods={holidays.data}
              quotasMap={quotasMap}
              selectedData={selectedQuotas}
              tableConfig={config}
              onSelectData={setSelectedQuotas}
              onChange={handleQuotaChange}
            />
          </div>
          {isNonEmptyArray(selectedQuotas.values) && (
            <QuotaToolBar
              selectedQuotas={selectionToQuotas(selectedQuotas)}
              onChange={handleQuotaChange}
              onUnselect={() => setSelectedQuotas(DEFAULT_SELECTED_VALUES)}
            />
          )}
        </div>
      </QuotaFormModalProvider>
    </div>
  )
}

export default SchoolQuotasView
